/**
 * 付费档位管理页（C1 验收页）。
 *
 * Phase 4-1 / Stream 4 / Plan §12 / Design §4.4.
 *
 * 功能：
 *  - 列表 + 状态过滤（all / active / archived）
 *  - 顶部"新增档位"按钮（modal: id + name + priceCents + bookCount + tag + description + displayOrder）
 *  - 行内操作：编辑（modal）/ 上架 / 下架
 *  - 编辑 modal 在 active 状态下 priceCents / bookCount 输入框 disabled + Alert 提示
 *    （UI 提示是体验层，真正校验在后端 51201 + "上架中不可改价"）
 *  - 各操作按钮用 PermissionGate 包裹
 *
 * 权限：
 *  - package:read    super_admin / operator
 *  - package:update  super_admin / operator
 *  - package:publish super_admin / operator
 *  （support / finance 全部不可见，访问后端会 403 + 51101）
 *
 * 业务规则：active 调价、版本冲突、ID 重复 → 后端 51201 + 友好 message；
 * 前端 onError 中读取 e.response.data.message 直接展示。
 */
import { useMemo, useState } from 'react';
import {
  Table,
  Form,
  Input,
  InputNumber,
  Button,
  Space,
  Tag,
  Modal,
  Alert,
  Card,
  message,
  Popconfirm,
  Tabs,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listPackages,
  createPackage,
  updatePackage,
  publishPackage,
  archivePackage,
  type PackageDto,
  type PackageStatus,
  type ListPackagesFilter,
} from '../../api/packages';

type StatusTabKey = 'all' | 'active' | 'archived';

function statusColor(status: PackageStatus): string {
  return status === 'active' ? 'green' : 'default';
}

function statusLabel(status: PackageStatus): string {
  return status === 'active' ? '上架' : '下架';
}

function formatYuan(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

/**
 * antd Form.Item 元 ↔ 分 双向转换 props（display 用元，store 用分整数）。
 *
 * normalize: 元 → 分（×100 + Math.round 避免浮点误差 e.g. 79.01 * 100 = 7901.000…0001）
 * getValueProps: 分 → 元（÷100，给 InputNumber 显示）
 *
 * 用法：
 *   <Form.Item name="priceCents" {...yuanCentsFieldProps}>
 *     <InputNumber min={0} step={0.01} precision={2} addonBefore="¥" />
 *   </Form.Item>
 */
const yuanCentsFieldProps = {
  normalize: (yuan: number | string | undefined) => {
    if (yuan === undefined || yuan === null || yuan === '') return undefined;
    const n = typeof yuan === 'number' ? yuan : parseFloat(yuan);
    if (Number.isNaN(n)) return undefined;
    return Math.round(n * 100);
  },
  getValueProps: (cents: number | undefined) => ({
    value: cents === undefined ? undefined : cents / 100,
  }),
};

function PackagesPageInner() {
  const qc = useQueryClient();
  const [statusTab, setStatusTab] = useState<StatusTabKey>('all');

  // 新建 modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm<{
    id: string;
    name: string;
    description?: string;
    priceCents: number;
    bookCount: number;
    tag?: string;
    displayOrder?: number;
  }>();

  // 编辑 modal
  const [editTarget, setEditTarget] = useState<PackageDto | null>(null);
  const [editForm] = Form.useForm<{
    name: string;
    description?: string;
    priceCents: number;
    bookCount: number;
    tag?: string;
    displayOrder: number;
  }>();

  const filter: ListPackagesFilter = useMemo(
    () => (statusTab === 'all' ? {} : { status: statusTab }),
    [statusTab],
  );

  const listQuery = useQuery({
    queryKey: ['packages', filter],
    queryFn: () => listPackages(filter),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['packages'] });
  }

  function errorHandler(label: string) {
    return (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? label);
    };
  }

  const createMutation = useMutation({
    mutationFn: createPackage,
    onSuccess: () => {
      message.success('档位已创建（默认下架，请确认价格后上架）');
      setCreateOpen(false);
      createForm.resetFields();
      invalidate();
    },
    onError: errorHandler('创建失败'),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; patch: Parameters<typeof updatePackage>[1] }) =>
      updatePackage(vars.id, vars.patch),
    onSuccess: () => {
      message.success('档位已更新');
      setEditTarget(null);
      editForm.resetFields();
      invalidate();
    },
    onError: errorHandler('更新失败'),
  });

  const publishMutation = useMutation({
    mutationFn: (vars: { id: string; version: number }) =>
      publishPackage(vars.id, vars.version),
    onSuccess: () => {
      message.success('已上架');
      invalidate();
    },
    onError: errorHandler('上架失败'),
  });

  const archiveMutation = useMutation({
    mutationFn: (vars: { id: string; version: number }) =>
      archivePackage(vars.id, vars.version),
    onSuccess: () => {
      message.success('已下架');
      invalidate();
    },
    onError: errorHandler('下架失败'),
  });

  function openEdit(row: PackageDto) {
    setEditTarget(row);
    editForm.setFieldsValue({
      name: row.name,
      description: row.description ?? undefined,
      priceCents: row.priceCents,
      bookCount: row.bookCount,
      tag: row.tag ?? undefined,
      displayOrder: row.displayOrder,
    });
  }

  function submitCreate() {
    createForm.validateFields().then((v) => {
      createMutation.mutate({
        id: v.id,
        name: v.name,
        description: v.description?.trim() ? v.description.trim() : null,
        priceCents: v.priceCents,
        bookCount: v.bookCount,
        tag: v.tag?.trim() ? v.tag.trim() : null,
        displayOrder: v.displayOrder ?? 0,
      });
    });
  }

  function submitEdit() {
    if (!editTarget) return;
    editForm.validateFields().then((v) => {
      const patch: Parameters<typeof updatePackage>[1] = {
        version: editTarget.version,
      };
      if (v.name !== editTarget.name) patch.name = v.name;
      const nextDesc = v.description?.trim() ? v.description.trim() : null;
      if (nextDesc !== editTarget.description) patch.description = nextDesc;
      const nextTag = v.tag?.trim() ? v.tag.trim() : null;
      if (nextTag !== editTarget.tag) patch.tag = nextTag;
      if (v.displayOrder !== editTarget.displayOrder) patch.displayOrder = v.displayOrder;
      // active 时 priceCents / bookCount input 已 disabled，但 form value 可能仍带原值
      // 这里显式跳过避免后端 51201 误拒（active 时即使值相同也别送）
      if (editTarget.status !== 'active') {
        if (v.priceCents !== editTarget.priceCents) patch.priceCents = v.priceCents;
        if (v.bookCount !== editTarget.bookCount) patch.bookCount = v.bookCount;
      }
      // 检查除 version 外是否有变化
      if (Object.keys(patch).length === 1) {
        message.info('无变化');
        setEditTarget(null);
        return;
      }
      updateMutation.mutate({ id: editTarget.id, patch });
    });
  }

  const columns: ColumnsType<PackageDto> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      ellipsis: true,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 160,
    },
    {
      title: '价格',
      dataIndex: 'priceCents',
      key: 'priceCents',
      width: 110,
      render: (v: number) => formatYuan(v),
    },
    {
      title: '本数',
      dataIndex: 'bookCount',
      key: 'bookCount',
      width: 80,
    },
    {
      title: '标签',
      dataIndex: 'tag',
      key: 'tag',
      width: 110,
      render: (v: string | null) => (v ? <Tag color="orange">{v}</Tag> : '-'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: PackageStatus) => <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>,
    },
    {
      title: '排序',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 70,
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      width: 70,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 240,
      render: (_: unknown, row: PackageDto) => (
        <Space size="small" wrap>
          <PermissionGate point={PERMISSIONS.PACKAGE_UPDATE}>
            <Button
              size="small"
              onClick={() => openEdit(row)}
              data-testid={`pkg-edit-${row.id}`}
            >
              编辑
            </Button>
          </PermissionGate>
          <PermissionGate point={PERMISSIONS.PACKAGE_PUBLISH}>
            {row.status === 'archived' ? (
              <Popconfirm
                title="确认上架？"
                description="上架后将不可改价 / 改本数，请先确认价格与本数。"
                onConfirm={() =>
                  publishMutation.mutate({ id: row.id, version: row.version })
                }
              >
                <Button size="small" type="primary" data-testid={`pkg-publish-${row.id}`}>
                  上架
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="确认下架？"
                description="下架后用户将看不到该档位，但已下单的不受影响。"
                onConfirm={() =>
                  archiveMutation.mutate({ id: row.id, version: row.version })
                }
              >
                <Button size="small" danger data-testid={`pkg-archive-${row.id}`}>
                  下架
                </Button>
              </Popconfirm>
            )}
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <Card title="付费档位管理（C1）" data-testid="packages-page">
      <Space
        style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}
      >
        <Tabs
          activeKey={statusTab}
          onChange={(k) => setStatusTab(k as StatusTabKey)}
          items={[
            { key: 'all', label: '全部' },
            { key: 'active', label: '已上架' },
            { key: 'archived', label: '已下架' },
          ]}
          data-testid="packages-status-tabs"
        />
        <PermissionGate point={PERMISSIONS.PACKAGE_UPDATE}>
          <Button
            type="primary"
            onClick={() => setCreateOpen(true)}
            data-testid="packages-create-btn"
          >
            新增档位
          </Button>
        </PermissionGate>
      </Space>

      <Table<PackageDto>
        rowKey="id"
        loading={listQuery.isLoading}
        dataSource={listQuery.data?.items ?? []}
        columns={columns}
        pagination={false}
        data-testid="packages-table"
      />

      {/* 新增 modal */}
      <Modal
        title="新增档位"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        onOk={submitCreate}
        confirmLoading={createMutation.isPending}
        destroyOnClose
        data-testid="packages-create-modal"
      >
        <Alert
          type="info"
          showIcon
          message="新档位默认 下架 状态，可编辑确认信息后上架"
          style={{ marginBottom: 16 }}
        />
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            label="ID"
            name="id"
            tooltip="例 pkg_1book_79；字母/数字/下划线/横线，1-64 字符。与小程序硬编码 packageId 风格对齐。"
            rules={[
              { required: true, message: '请填写档位 ID' },
              {
                pattern: /^[A-Za-z0-9_\-]{1,64}$/,
                message: '只能含字母/数字/下划线/横线，长度 1-64',
              },
            ]}
          >
            <Input placeholder="pkg_1book_79" />
          </Form.Item>
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请填写档位名称' }]}
          >
            <Input placeholder="例 单本套餐" />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={2} placeholder="选填" />
          </Form.Item>
          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              label="价格（元）"
              name="priceCents"
              rules={[{ required: true, message: '请填写价格' }]}
              tooltip="服务端以分存储；此处以元输入"
              {...yuanCentsFieldProps}
            >
              <InputNumber min={0} step={0.01} precision={2} addonBefore="¥" />
            </Form.Item>
            <Form.Item
              label="本数"
              name="bookCount"
              rules={[{ required: true, message: '请填写本数' }]}
            >
              <InputNumber min={1} step={1} />
            </Form.Item>
            <Form.Item label="排序" name="displayOrder" initialValue={0}>
              <InputNumber step={1} />
            </Form.Item>
          </Space>
          <Form.Item label="标签" name="tag">
            <Input placeholder="例 推荐 / 最划算（选填）" maxLength={32} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑 modal */}
      <Modal
        title={`编辑档位 ${editTarget?.id ?? ''}`}
        open={editTarget !== null}
        onCancel={() => {
          setEditTarget(null);
          editForm.resetFields();
        }}
        onOk={submitEdit}
        confirmLoading={updateMutation.isPending}
        destroyOnClose
        data-testid="packages-edit-modal"
      >
        {editTarget?.status === 'active' && (
          <Alert
            type="warning"
            showIcon
            message="上架中的档位不可改价 / 改本数"
            description="如需调价或改本数，请先下架；价格本数字段已锁定。"
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={editForm} layout="vertical" preserve={false}>
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请填写档位名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              label="价格（元）"
              name="priceCents"
              rules={[{ required: true, message: '请填写价格' }]}
              {...yuanCentsFieldProps}
            >
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                addonBefore="¥"
                disabled={editTarget?.status === 'active'}
              />
            </Form.Item>
            <Form.Item
              label="本数"
              name="bookCount"
              rules={[{ required: true, message: '请填写本数' }]}
            >
              <InputNumber
                min={1}
                step={1}
                disabled={editTarget?.status === 'active'}
              />
            </Form.Item>
            <Form.Item
              label="排序"
              name="displayOrder"
              rules={[{ required: true, message: '请填写排序' }]}
            >
              <InputNumber step={1} />
            </Form.Item>
          </Space>
          <Form.Item label="标签" name="tag">
            <Input maxLength={32} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

export default function PackagesPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.PACKAGE_READ}
      fallback={
        <Card>
          <Alert
            type="error"
            showIcon
            message="无访问权限"
            description="付费档位管理（C1）需要 package:read 权限，请联系超管。"
          />
        </Card>
      }
    >
      <PackagesPageInner />
    </PermissionGate>
  );
}
